import { refreshAdmin } from './admin-auth-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';

export const LUYOU_WORKBOOK_TEMPLATE_MAX_BYTES = 5 * 1024 * 1024;

const TEMPLATE_ENDPOINT =
  '/api/v1/admin/agent-profiles/luyou_expert/workbook-template';

export interface LuyouWorkbookTemplate {
  configured: boolean;
  profileId: string;
  revision: number | null;
  filename: string | null;
  bytesSize: number | null;
  updatedAt: string | null;
}

interface ApiLuyouWorkbookTemplate {
  configured: boolean;
  profile_id: string;
  revision: number | null;
  filename: string | null;
  bytes_size: number | null;
  updated_at: string | null;
}

let adminSessionRefreshPromise: Promise<string | null> | null = null;

function emptyTemplate(): LuyouWorkbookTemplate {
  return {
    configured: false,
    profileId: 'luyou_expert',
    revision: null,
    filename: null,
    bytesSize: null,
    updatedAt: null,
  };
}

function normalizeTemplate(
  payload: ApiLuyouWorkbookTemplate,
): LuyouWorkbookTemplate {
  return {
    configured: payload.configured,
    profileId: payload.profile_id,
    revision: payload.revision,
    filename: payload.filename,
    bytesSize: payload.bytes_size,
    updatedAt: payload.updated_at,
  };
}

async function readErrorMessage(response: Response): Promise<string> {
  const fallback = response.statusText || 'Request failed.';
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as { detail?: unknown };
      if (typeof payload.detail === 'string' && payload.detail.trim()) {
        return payload.detail;
      }
      if (payload.detail != null) {
        return JSON.stringify(payload.detail);
      }
      return JSON.stringify(payload);
    } catch {
      return fallback;
    }
  }

  const text = await response.text().catch(() => fallback);
  return text || fallback;
}

async function refreshAdminAccessToken(): Promise<string | null> {
  if (adminSessionRefreshPromise) {
    return adminSessionRefreshPromise;
  }

  adminSessionRefreshPromise = refreshAdmin()
    .then((session) => {
      useAdminAuthStore.getState().setSession(session.accessToken, session.user);
      return session.accessToken;
    })
    .catch(() => {
      useAdminAuthStore.getState().clearSession();
      return null;
    })
    .finally(() => {
      adminSessionRefreshPromise = null;
    });

  return adminSessionRefreshPromise;
}

function sendAdminRequest(
  accessToken: string,
  init: RequestInit,
): Promise<Response> {
  return fetch(TEMPLATE_ENDPOINT, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(init.headers ?? {}),
    },
  });
}

async function requestAdminTemplate(
  accessToken: string,
  init: RequestInit,
  actionLabel: string,
): Promise<Response> {
  let response = await sendAdminRequest(accessToken, init);

  if (response.status === 401) {
    const refreshedAccessToken = await refreshAdminAccessToken();
    if (refreshedAccessToken) {
      response = await sendAdminRequest(refreshedAccessToken, init);
    }
  }

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`${actionLabel} failed (${response.status}): ${message}`);
  }

  return response;
}

async function readTemplateResponse(
  response: Response,
): Promise<LuyouWorkbookTemplate> {
  if (response.status === 204) {
    return emptyTemplate();
  }
  return normalizeTemplate((await response.json()) as ApiLuyouWorkbookTemplate);
}

export function validateLuyouWorkbookTemplateFile(file: File): string | null {
  if (!file.name.toLocaleLowerCase().endsWith('.xlsx')) {
    return '仅支持 .xlsx 文件。';
  }
  if (file.size === 0) {
    return '所选文件为空。';
  }
  if (file.size > LUYOU_WORKBOOK_TEMPLATE_MAX_BYTES) {
    return '文件不能超过 5 MiB。';
  }
  return null;
}

export async function getLuyouWorkbookTemplate(
  accessToken: string,
): Promise<LuyouWorkbookTemplate> {
  const response = await requestAdminTemplate(
    accessToken,
    { method: 'GET' },
    'Load Luyou workbook template',
  );
  return readTemplateResponse(response);
}

export async function uploadLuyouWorkbookTemplate(
  accessToken: string,
  file: File,
): Promise<LuyouWorkbookTemplate> {
  const validationMessage = validateLuyouWorkbookTemplateFile(file);
  if (validationMessage) {
    throw new Error(validationMessage);
  }

  const body = new FormData();
  body.append('file', file);
  const response = await requestAdminTemplate(
    accessToken,
    { method: 'PUT', body },
    'Upload Luyou workbook template',
  );
  return readTemplateResponse(response);
}

export async function deleteLuyouWorkbookTemplate(
  accessToken: string,
): Promise<void> {
  await requestAdminTemplate(
    accessToken,
    { method: 'DELETE' },
    'Delete Luyou workbook template',
  );
}
